import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { apiClient } from "../lib/api";

export interface TransferCountry {
  name: string;
  englishName: string;
  flag1x1: string;
  flag4x3: string;
}

export interface TransferTeam {
  id: number;
  externalId: string;
  slug: string;
  title: string;
  englishName: string;
  fullTitle: string;
  logo: string;
  thumbnail: string;
  isNational: boolean;
  country?: TransferCountry | null;
}

export interface TransferCompetition {
  id: number;
  externalId: string;
  title: string;
  englishName: string;
  slug: string;
  seoSlug: string;
  logo: string;
  thumbnail: string;
}

export interface TransferPlayer {
  id: number;
  externalId: string;
  slug: string;
  fullname: string;
  englishName: string;
  image: string;
  positionKey: string;
  positionValue: string;
  kitNumber?: number | null;
}

export interface Transfer {
  id: number;
  externalId: string;
  transferSeason: string;
  player: TransferPlayer;
  fromTeam?: TransferTeam | null;
  toTeam?: TransferTeam | null;
  fromCompetition?: TransferCompetition | null;
  toCompetition?: TransferCompetition | null;
  transferTime: number;
  fromDate: number;
  toDate: number;
  transferFee?: number | null;
  transferCurrencyUnit: string;
  fullTransferFee: string;
  completed: boolean;
  isImportant: boolean;
  transferType: string;
  transferTitle: string;
  statusId: number;
  statusName: string;
}

export interface TransfersListResponse {
  count: number;
  limit: number;
  offset: number;
  data: Transfer[];
}

export interface TransfersFilters {
  competition?: string;
  team?: string;
  player?: string;
  important?: boolean;
  offset?: number;
  limit?: number;
}

export const useTransferCompetitions = (
  options?: Omit<UseQueryOptions<TransferCompetition[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: ["transfers", "competitions"],
    queryFn: async () => {
      const { data } = await apiClient.get<TransferCompetition[]>(
        "/transfers/competitions"
      );
      return data;
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const useTransferTeams = (
  competitionExternalId: string | undefined,
  options?: Omit<UseQueryOptions<TransferTeam[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: ["transfers", "teams", competitionExternalId ?? ""],
    queryFn: async () => {
      if (!competitionExternalId) return [];
      const { data } = await apiClient.get<TransferTeam[]>(
        `/transfers/competitions/${competitionExternalId}/teams`
      );
      return data;
    },
    enabled: Boolean(competitionExternalId),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const useTransfers = (
  filters: TransfersFilters,
  options?: Omit<UseQueryOptions<TransfersListResponse, Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: ["transfers", "list", filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.competition) params.competition = filters.competition;
      if (filters.team) params.team = filters.team;
      if (filters.player) params.player = filters.player;
      if (filters.important) params.important = true;
      params.offset = filters.offset ?? 0;
      params.limit = filters.limit ?? 24;

      const { data } = await apiClient.get<TransfersListResponse>("/transfers", {
        params,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

// Detail payload from upstream profile. Loosely typed because the upstream
// schema is large; the page picks only the fields it can render.
export interface TransferPlayerDetailPayload {
  id?: string;
  player_id?: string;
  fullname?: string;
  slug?: string;
  image?: string;
  career_statistics?: Array<{
    id: string;
    display_name: string;
    logo: string;
    thumbnail?: string;
    competition_trends?: Array<{
      competition_trend: string;
      teams?: Array<{
        team?: {
          id: string;
          display_name: string;
          logo: string;
          full_title?: string;
        };
        stats?: Array<{
          category: string;
          display_name: string;
          metrics?: Array<{
            key: string;
            value: number;
            display_name: string;
          }>;
        }>;
      }>;
    }>;
  }>;
  career_competitions?: Array<{
    id: string;
    display_name: string;
    logo: string;
    thumbnail?: string;
  }>;
  last_updated_at?: number;
}

export interface TransferPlayerResponse {
  player: TransferPlayer;
  transfers: Transfer[];
  detail: TransferPlayerDetailPayload | null;
}

export const useTransferPlayer = (
  externalId: string | undefined,
  options?: Omit<UseQueryOptions<TransferPlayerResponse, Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: ["transfers", "player", externalId ?? ""],
    queryFn: async () => {
      const { data } = await apiClient.get<TransferPlayerResponse>(
        `/transfers/players/${encodeURIComponent(externalId!)}`
      );
      return data;
    },
    enabled: Boolean(externalId),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};

export interface TransferTeamResponse {
  team: TransferTeam;
  transfers: Transfer[];
}

export const useTransferTeam = (
  externalId: string | undefined,
  options?: Omit<UseQueryOptions<TransferTeamResponse, Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: ["transfers", "team", externalId ?? ""],
    queryFn: async () => {
      const { data } = await apiClient.get<TransferTeamResponse>(
        `/transfers/teams/${externalId}`
      );
      return data;
    },
    enabled: Boolean(externalId),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};
